<template>
    <!-- Page Header -->
    <div class="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
      <div class="flex items-center gap-4">
        <div class="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-700 rounded-2xl
                    flex items-center justify-center shadow-lg shadow-orange-500/30">
          <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
          </svg>
        </div>
        <div>
          <h1 class="text-2xl font-bold text-slate-900">{{ gallery.id ? 'Update Image' : 'Add New Image' }}</h1>
          <p class="text-sm text-slate-500 mt-0.5">Manage your gallery images</p>
        </div>
      </div>
    </div>

    <!-- Form Card -->
    <div class="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden max-w-2xl">

      <!-- Card Header -->
      <div class="px-6 py-4 bg-gradient-to-r from-orange-500 to-orange-700 border-b border-orange-600">
        <div class="flex items-center gap-3">
          <svg class="w-5 h-5 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
          </svg>
          <span class="text-white font-semibold text-sm">Image Details</span>
        </div>
      </div>

      <!-- Loading Spinner -->
      <div v-if="loading" class="flex items-center justify-center py-24">
        <div class="flex flex-col items-center gap-3">
          <div class="w-10 h-10 border-4 border-orange-200 border-t-orange-600 rounded-full animate-spin"></div>
          <p class="text-sm text-slate-400">Loading…</p>
        </div>
      </div>

      <!-- Form -->
      <form v-else @submit.prevent="onSubmit" class="p-6 space-y-6">

        <!-- Image Preview -->
        <div v-if="previewUrl" class="rounded-xl overflow-hidden border border-slate-200 shadow-sm flex justify-center bg-slate-50/50 p-4">
          <div class="relative group rounded-lg overflow-hidden border border-slate-200">
             <img
                :src="previewUrl"
                alt="Preview"
                class="w-64 h-64 object-cover object-center"
             />
             <div class="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
                <span class="text-white font-medium text-sm">Preview</span>
             </div>
          </div>
        </div>

        <!-- File Upload -->
        <div>
          <label class="block text-sm font-semibold text-slate-700 mb-2">
            <span class="flex items-center gap-1.5">
              <svg class="w-4 h-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"/>
              </svg>
              Upload Image
            </span>
          </label>
          <div class="relative">
            <input
              type="file"
              accept="image/*"
              @change="handleFileChange"
              class="block w-full text-sm text-slate-500
                     file:mr-4 file:py-2.5 file:px-4
                     file:rounded-xl file:border-0
                     file:text-sm file:font-semibold
                     file:bg-orange-50 file:text-orange-700
                     hover:file:bg-orange-100
                     border border-slate-200 rounded-xl
                     cursor-pointer transition-all duration-200"
              :class="{ 'border-red-400': errors.url }"
            />
          </div>
          <p v-if="errors.url" class="mt-1.5 text-xs text-red-500 font-medium">{{ errors.url[0] }}</p>
          <p class="mt-1.5 text-xs text-slate-400">Accepted formats: JPG, PNG, GIF</p>
        </div>

        <!-- Actions -->
        <div class="flex items-center justify-end gap-3 pt-2 border-t border-slate-100">
          <router-link
            :to="{ name: 'app.galleries' }"
            class="px-4 py-2.5 text-sm font-semibold text-slate-600 border border-slate-200 rounded-xl
                   hover:bg-slate-50 hover:border-slate-300 transition-all duration-200"
          >
            Cancel
          </router-link>
          <button
            type="button"
            @click="onSubmit($event, true)"
            :disabled="loading"
            class="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-orange-500 to-orange-600
                   hover:from-orange-600 hover:to-orange-700 text-white text-sm font-semibold rounded-xl
                   shadow-md shadow-orange-500/25 transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5
                   disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
          >
            <svg v-if="loading" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"/>
            </svg>
            <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
            </svg>
            {{ gallery.id ? 'Update Image' : 'Save Image' }}
          </button>
        </div>
      </form>
    </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import store from "@/store";
import { useToast } from "primevue/usetoast";

const toast = useToast();

const showSuccess = (detail) => {
  toast.add({
    severity: "success",
    summary: "Success",
    detail,
    life: 3000,
  });
};

const route = useRoute();
const router = useRouter();

const loading = ref(false);
const gallery = ref({
  id: null,
  url: "",
});

const previewUrl = ref(null);
const errors = ref({
  url: [],
});

function handleFileChange(e) {
  const file = e.target.files[0];
  if (file) {
    gallery.value.url = file;
    previewUrl.value = URL.createObjectURL(file);
  }
}

function onSubmit() {
  loading.value = true;
  if (gallery.value.id) {
    store
      .dispatch("updateGallery", gallery.value)
      .then(() => {
        loading.value = false;
        router.push({ name: "app.galleries" });
        showSuccess("Image updated successfully");
      })
      .catch(handleError);
  } else {
    store.dispatch("createGallery", gallery.value)
      .then(() => {
        loading.value = false;
        router.push({ name: "app.galleries" });
        showSuccess("Image added successfully");
      })
      .catch(handleError);
  }
}

function handleError(err) {
  loading.value = false;
  if (err.response?.status === 422) {
    errors.value = err.response.data.errors;
  } else if (err.response?.status === 409) {
    toast.add({ severity: 'error', summary: 'Error', detail: err.response.data.message });
  } else {
    console.error("Error submitting form:", err);
  }
}

onMounted(() => {
  if (route.params.id) {
    loading.value = true;
    store.dispatch("getGallery", route.params.id)
      .then((res) => {
        gallery.value = res.data ?? res; // Safety check for data unwrapping
        previewUrl.value = gallery.value.url;
      })
      .catch((error) => {
        console.error("An error occurred:", error);
      })
      .finally(() => {
        loading.value = false;
      });
  }
});
</script>
